import React, { useMemo } from 'react';
import { Play, Square, RotateCcw, Maximize2, RefreshCw, AlertTriangle, Loader2, MonitorX } from 'lucide-react';
import { useLang, tx } from '../../i18n/LanguageContext';
import { buildSimDoc, isWebGLAvailable, ASPECTS } from '../../lib/simFrame';

const Ctrl = ({ onClick, Icon, label, cls, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick} aria-label={label} title={label}
    className={`h-11 min-w-[44px] px-3 sm:px-4 rounded-xl inline-flex items-center justify-center gap-2 text-sm font-bold border backdrop-blur transition-transform active:scale-95 ${cls}`}>
    <Icon className="w-4 h-4" /><span className="hidden sm:inline">{label}</span>
  </button>
);

const Overlay = ({ Icon, title, desc, action, spin, testId }) => (
  <div data-testid={testId} className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-900/80 text-white text-center p-6">
    <Icon className={`w-10 h-10 ${spin ? 'animate-spin' : ''}`} />
    <p className="font-bold">{title}</p>
    {desc && <p className="text-sm text-slate-300 max-w-sm">{desc}</p>}
    {action}
  </div>
);

export const SimStage = ({ sim, frameRef, bridge, runKey, onReload }) => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const doc = useMemo(() => buildSimDoc(sim.html, bridge.nonce), [sim.html, bridge.nonce]);
  const noGL = useMemo(() => sim.render_mode === '3d' && !isWebGLAvailable(), [sim.render_mode]);
  const { status, error, can, send } = bridge;
  const full = () => frameRef.current?.parentElement?.requestFullscreen?.();
  const reset = () => (can('reset') ? send('reset') : onReload());
  const retry = <button type="button" data-testid="sim-retry-btn" onClick={onReload} className="h-11 px-5 rounded-xl bg-white text-slate-900 font-bold inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" />{L('إعادة المحاولة', 'Try again')}</button>;

  return (
    <div data-testid="sim-stage" className="rounded-3xl bg-white border border-slate-200 p-2.5 sm:p-3 shadow-sm">
      <div className="relative w-full overflow-hidden rounded-2xl bg-slate-100 [&:fullscreen]:rounded-none [&:fullscreen]:bg-black" style={{ aspectRatio: ASPECTS[sim.aspect] || ASPECTS['16:9'] }}>
        {noGL ? (
          <Overlay testId="sim-webgl-fallback" Icon={MonitorX} title={L('جهازك لا يدعم الرسوم ثلاثية الأبعاد (WebGL)', 'Your device does not support 3D graphics (WebGL)')}
            desc={L('جرّب متصفحاً حديثاً مثل Chrome أو فعّل تسريع الرسوميات، أو افتح المحاكاة من جهاز آخر.', 'Try a modern browser such as Chrome, enable hardware acceleration, or use another device.')} />
        ) : (
          <iframe key={runKey} ref={frameRef} title={sim.titleAr || 'simulation'} data-testid="sim-iframe"
            sandbox="allow-scripts" referrerPolicy="no-referrer" srcDoc={doc}
            className="absolute inset-0 w-full h-full border-0 bg-white" />
        )}
        {!noGL && status === 'loading' && <Overlay testId="sim-loading" Icon={Loader2} spin title={L('جارٍ تحميل المحاكاة...', 'Loading simulation...')} />}
        {!noGL && status === 'timeout' && <Overlay testId="sim-load-error" Icon={AlertTriangle} title={L('تعذّر تحميل المحاكاة', 'The simulation could not load')} desc={L('تحقق من اتصالك ثم أعد المحاولة.', 'Check your connection and try again.')} action={retry} />}
        {!noGL && (
          <>
            {error && status !== 'timeout' && (
              <div data-testid="sim-runtime-error" className="absolute top-2.5 start-2.5 z-20 max-w-[60%] px-3 py-1.5 rounded-lg bg-rose-600/90 text-white text-xs font-bold inline-flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{L('خطأ داخل المحاكاة', 'Simulation error')}: {error}</span>
              </div>
            )}
          </>
        )}
      </div>
      {!noGL && (
        <div className="flex flex-wrap items-center gap-2 pt-3" data-testid="sim-controls">
          {can('start') && <Ctrl testId="sim-ctrl-start" onClick={() => send('start')} Icon={Play} label={L('تشغيل', 'Start')} cls="bg-emerald-600 border-emerald-600 text-white" />}
          {can('stop') && <Ctrl testId="sim-ctrl-stop" onClick={() => send('stop')} Icon={Square} label={L('إيقاف', 'Stop')} cls="bg-white border-rose-200 text-rose-600" />}
          <Ctrl testId="sim-ctrl-reset" onClick={reset} Icon={RotateCcw} label={L('إعادة ضبط', 'Reset')} cls="bg-white border-slate-200 text-slate-700" />
          <Ctrl testId="sim-reload-btn" onClick={onReload} Icon={RefreshCw} label={L('إعادة تحميل', 'Reload')} cls="bg-white border-slate-200 text-slate-700" />
          <Ctrl testId="sim-fullscreen-btn" onClick={full} Icon={Maximize2} label={L('ملء الشاشة', 'Fullscreen')} cls="bg-white border-slate-200 text-slate-700" />
          <span className="ms-auto text-xs text-slate-400" data-testid="sim-bridge-status">
            {status === 'ready' ? L('متصلة بالأسئلة', 'Linked to questions') : status === 'loaded' ? L('محاكاة مستقلة', 'Standalone simulation') : ''}
          </span>
        </div>
      )}
    </div>
  );
};
